import React, { useState, useEffect, useRef } from "react";
import { URLS, makeApi } from "Utils/apiURL";
import {
  POLL_INTERVAL_MS,
  VIEW_RESULT_LIMIT
} from "Pages/Dashboard/data-toolkit/constants";
import {
  unwrap,
  isTerminal,
  statusMeta,
  formatDate
} from "Pages/Dashboard/data-toolkit/dtHelpers";

const fmtN = n => Number(n || 0).toLocaleString();
const plural = (n, one, many) => `${fmtN(n)} ${n === 1 ? one : many}`;

/** "40 contacts updated · 3 changed jobs — 3 new contacts created, old ones marked Moved" */
const contactsSummary = job => {
  let text = `${plural(job.contactsUpdated, "contact", "contacts")} updated`;
  if (job.contactsMoved > 0) {
    text += ` · ${plural(job.contactsMoved, "person", "people")} changed jobs — ${plural(
      job.contactsCreated,
      "new contact",
      "new contacts"
    )} created, old ones marked Moved`;
  } else if (job.contactsCreated > 0) {
    text += ` · ${plural(job.contactsCreated, "new contact", "new contacts")} created`;
  }
  return text;
};

// Shared side drawer for a single run: live progress while it processes, then the
// results table + download once complete. Tool-specific bits are injected:
//   stats(counts, job) -> [{ label, value, brand? }]  the summary stat cards
//   columns            -> [{ header, cell(row) }]      the appended result columns
// (input columns are always rendered first, from each record's input map).
const ResultsDrawer = ({ jobId, onClose, onProcessed, stats, columns }) => {
  const [job, setJob] = useState(null);
  const [results, setResults] = useState([]);
  const [headers, setHeaders] = useState([]);
  const pollRef = useRef(null);
  const notifiedRef = useRef(false);

  useEffect(() => {
    const loadResults = async () => {
      // Preview is intentionally capped (the server also enforces this); the full
      // set is only available via download, keeping this in-memory table small.
      const res = await makeApi({
        url: `${URLS.dataToolkitResults.replace(
          "<<id>>",
          jobId
        )}?limit=${VIEW_RESULT_LIMIT}`,
        method: "GET"
      });
      const { status, data } = unwrap(res);
      if (status === 200 && Array.isArray(data)) {
        setResults(data);
        if (data.length && data[0].input) setHeaders(Object.keys(data[0].input));
      }
    };

    const tick = async () => {
      const res = await makeApi({
        url: URLS.dataToolkitJob.replace("<<id>>", jobId),
        method: "GET"
      });
      const { status, data } = unwrap(res);
      if (status === 200 && data) {
        setJob(data);
        // A run over contacts keeps polling briefly after it finishes, until its results have
        // been written back to the contacts.
        if (isTerminal(data.status) && !(data.status === "PROCESSED" && data.writeBackPending)) {
          clearInterval(pollRef.current);
          if (data.status === "PROCESSED") {
            loadResults();
            if (!notifiedRef.current && onProcessed) {
              notifiedRef.current = true;
              onProcessed();
            }
          }
        }
      }
    };

    tick();
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [jobId, onProcessed]);

  const download = () => {
    const a = document.createElement("a");
    a.href = URLS.dataToolkitDownload.replace("<<id>>", jobId);
    a.download = `${(job && job.name) || "data-toolkit"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const counts = (job && job.counts) || {};
  const total = counts.total || (job && job.inputRowCount) || 0;
  const processed =
    (counts.found || 0) +
    (counts.risky || 0) +
    (counts.notFound || 0) +
    (counts.skipped || 0) +
    (counts.failed || 0);
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const done = job && job.status === "PROCESSED";
  const failed = job && job.status === "FAILED";
  const meta = statusMeta(job && job.status);
  const statCards = stats ? stats(counts, job) : [];
  // columns may be a function of the job (e.g. account advanced fields).
  const cols = typeof columns === "function" ? columns(job || {}) : columns;

  // End-of-run credit breakdown (Data Magic sets this; other tools leave it null).
  const cs = done && job && job.creditSummary;
  const creditLines = cs
    ? [
        {
          label: "Contacts enriched",
          count: cs.contacts,
          credits: cs.contactCredits
        },
        {
          label: "Companies enriched",
          count: cs.companies,
          credits: cs.companyCredits
        },
        { label: "Emails found", count: cs.emails, credits: cs.emailCredits },
        {
          label: "Replacement contacts",
          count: cs.alternates,
          credits: cs.alternateCredits
        }
      ].filter(l => l.count > 0)
    : [];

  return (
    <div className="dtDrawerOverlay" onClick={onClose}>
      <div className="dtDrawer" onClick={e => e.stopPropagation()}>
        <div className="dtDrawerHead">
          <div>
            <div className="dtDrawerTitle">{(job && job.name) || "Run"}</div>
            <div className="dtDrawerSub">
              <span className={`dtStatusPill ${meta.cls}`}>{meta.label}</span>
              <span className="dtDot">·</span>
              {total} record{total === 1 ? "" : "s"}
              {job && job.createTs ? ` · ${formatDate(job.createTs)}` : ""}
            </div>
            {job && job.source === "CONTACTS" && (
              <div className="dtDrawerSub dtContactsNote">
                {job.updateMode === "NONE"
                  ? "Ran on your contacts · results aren't written to them"
                  : job.writeBackPending
                  ? "Updates your contacts when it finishes"
                  : contactsSummary(job)}
              </div>
            )}
          </div>
          <button className="dtIconBtn" onClick={onClose} title="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="dtDrawerBody">
          {failed ? (
            <div className="dtError">
              {(job && job.errorMessage) || "This run failed. Please try again."}
            </div>
          ) : (
            <>
              <div className="dtStatCards">
                {statCards.map((s, i) => (
                  <div className={`dtStat ${s.brand ? "brand" : ""}`} key={i}>
                    <div className="dtStatNum">{s.value}</div>
                    <div className="dtStatLabel">{s.label}</div>
                  </div>
                ))}
              </div>

              {!done && (
                <div className="dtProgressBar">
                  <div className="dtProgressFill" style={{ width: `${pct}%` }} />
                </div>
              )}

              {done && creditLines.length > 0 && (
                <div className="dtCreditSummary">
                  <div className="dtCreditSummaryHead">Credits used</div>
                  {creditLines.map((l, i) => (
                    <div className="dtCreditLine" key={i}>
                      <span className="dtCreditLbl">
                        {l.label}
                        <span className="dtCreditCount">
                          {" "}
                          {l.count.toLocaleString()}
                        </span>
                      </span>
                      <span className="dtCreditVal">
                        {l.credits.toLocaleString()}
                      </span>
                    </div>
                  ))}
                  <div className="dtCreditLine dtCreditTotal">
                    <span className="dtCreditLbl">Total charged</span>
                    <span className="dtCreditVal">
                      {(cs.total || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {done && (
                <>
                  <div className="dtActions" style={{ margin: "6px 0 16px" }}>
                    <button className="dtBtn primary" onClick={download}>
                      Download CSV
                    </button>
                  </div>
                  {results.length > 0 && (
                    <div className="dtTableWrap">
                      <table className="dtTable">
                        <thead>
                          <tr>
                            {headers.map((h, i) => (
                              <th key={i}>{h}</th>
                            ))}
                            {cols.map((c, i) => (
                              <th key={`c${i}`}>{c.header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {results.map((r, ri) => (
                            <tr key={ri}>
                              {headers.map((h, i) => (
                                <td key={i}>{(r.input && r.input[h]) || ""}</td>
                              ))}
                              {cols.map((c, i) => (
                                <td key={`c${i}`}>{c.cell(r)}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {total > results.length && (
                    <div className="dtPreviewNote">
                      Showing the first {results.length} of{" "}
                      {total.toLocaleString()} records. Download the CSV for the
                      full results.
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsDrawer;
